import { useState, useEffect, useRef, createRef } from 'react';
import { useLocation } from 'react-router-dom';
import Basic from 'components/pages/modal/Basic';
import styles from 'styles/components/common/overlays/Modal.module.scss';
import classNames from 'classnames';
import useModal from 'hooks/useModal';

type TProps = {
  onClick?: () => void;
  onClickOutside?: () => void;
};

const Modal = ({ onClick, onClickOutside }: TProps) => {
  const [modalRef, setModalRef] =
    useState<React.RefObject<HTMLDivElement> | null>(null);
  const childRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const location = useLocation();
  const { modal, closeModal } = useModal();
  const currentModal = modal.modalStack[modal.modalStack.length - 1] || {};

  useEffect(() => {
    setModalRef(createRef<HTMLDivElement>());
  }, [modal.modalStack.length]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [modalRef]);

  useEffect(() => {
    if (location.pathname !== currentModal?.location) {
      closeModal();
    }
  }, [location.pathname]);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  const checkOverflow = () => {
    if (childRef.current) {
      const childHeight = childRef.current.offsetHeight;
      const windowHeight = window.innerHeight;

      setIsOverflow(childHeight > windowHeight - 40);
    }
  };

  const getScrollBarWidth = () => {
    const div = document.createElement('div');
    div.style.visibility = 'block';
    div.style.overflow = 'scroll';
    div.style.width = '50px';
    div.style.height = '50px';
    document.body.appendChild(div);

    const scrollBarWidth = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
    return scrollBarWidth;
  };

  document.body.style.overflow = 'hidden';
  document.body.style.paddingRight = `${getScrollBarWidth()}px`;

  const onClickModalClose = () => {
    if (onClick) {
      onClick();
    }
    closeModal();
  };

  const handleClick = (event: MouseEvent) => {
    if (modalRef?.current && modalRef.current.contains(event.target as Node))
      return;
    onClickOutside ? onClickOutside() : onClickModalClose();
  };

  return (
    <>
      <div className="dimmed" />
      <div className={styles.modal_container}>
        {modal.modalStack?.map((currentModal, index) => (
          <div
            ref={modalRef}
            key={index}
            className={classNames(
              'z_99',
              !isOverflow ? 'position_align_center' : styles.overflow_modal
            )}
          >
            {currentModal.element ? (
              <div ref={childRef}>{currentModal.element}</div>
            ) : (
              <Basic
                isConfirm={currentModal.isConfirm}
                title={currentModal.title}
                text={currentModal.text ?? ''}
                cancelBtnText={currentModal.cancelBtnText}
                submitBtnText={currentModal.submitBtnText}
                onCancel={
                  !currentModal.isConfirm && onClickOutside
                    ? onClickOutside
                    : currentModal.onCancel
                }
                onSubmit={currentModal.onSubmit}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default Modal;
